import React, { useEffect, useState } from 'react';
import { Form } from 'mobx-react-form';
import { observer } from 'mobx-react-lite';
import { Button, Box, styled, CircularProgress, Snackbar, Alert } from '@mui/material';
import { isEmpty } from 'lodash';

import { muiFormBindings } from 'forms/utils/muiFormBindings';
import { useRootStore } from 'stores/RootStore';
import { setFormInitialValues, getChangedFormValues } from './utils';
import { FormOptions } from './types';

const FormFooter = styled('div')(({ theme }) => ({
  height: '64px',
  padding: '0 64px',
  borderTop: `1px solid #D0D4D9`,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  position: 'sticky',
  bottom: 0,
  width: '100%',
  background: 'rgba(250, 250, 250, 1)',
}));

const FormOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
});

/*
 * This is a wrapper for the form.
 * The `fields` array should match the structure of `UnderwritingOutput`
 * until custom mappers are implemented. Each field should be referenced
 * by its full path in the UI.
 *
 * Arrays of objects are defined as follows:
 * 1. Define the array field itself.
 * 2. Define the fields for an object within the array (at the same level
 *    as the array field). The naming convention is:
 *    `arrayFieldName[].objectFieldName`.
 *
 * Example:
 * {
 *   name: 'salesHistory',
 *   type: 'array',
 *   value: [],
 * },
 * {
 *   name: 'salesHistory[].uniqueSaleId',
 *   bindings: 'TextField',
 *   value: '',
 * },
 * {
 *   name: 'salesHistory[].date',
 *   type: 'date',
 *   bindings: 'TextField',
 *   value: '',
 * }
 */
export const wrapForm = (options: FormOptions) => {
  return function <P extends object>(WrappedComponent: React.ComponentType<P & { form: Form }>) {
    return observer((props: P) => {
      const { underwritingPageStore } = useRootStore();
      const initialData = underwritingPageStore.underwritingData;

      const [notification, setNotification] = useState<{
        show: boolean;
        message: string;
        severity: 'success' | 'error';
      }>({
        show: false,
        message: '',
        severity: 'success',
      });

      const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, show: false }));
      };

      const form = React.useMemo(() => {
        const formInstance = new Form(
          { fields: options.fields },
          {
            plugins: options.plugins,
            bindings: { ...muiFormBindings, ...(options.bindings || {}) },
            options: {
              ...options.options,
              submitThrowsError: true,
              // submitOnChange: true,
            },
            hooks: {
              ...options.hooks,

              /*
               * This is the only reliable way to handle the `submitting` state.
               * For some reason, even awaiting a promise in `onSubmit` does not work—
               * the `onSuccess` hook gets called before the promise is resolved.
               */
              onSuccess: async (form: Form) => {
                let isError = false;
                try {
                  const formData = getChangedFormValues(form, options.fields, initialData);

                  console.log('Form data (nested, modified fields only):', JSON.stringify(formData, null, 2));
                  await underwritingPageStore.saveUnderwriting(formData);
                } catch (error) {
                  isError = true;
                  console.error('Failed to save underwriting:', error);
                } finally {
                  setNotification({
                    show: true,
                    message: isError ? 'Failed to submit form. Please try again.' : 'Form submitted successfully',
                    severity: isError ? 'error' : 'success',
                  });
                }
              },
              // onError: (form: Form, error: any) => {
              //   console.error('Form submission failed:', error);
              //   if (options.hooks?.onError) {
              //     options.hooks.onError(form);
              //   }
              // },
            },
          }
        );

        return formInstance;
      }, [initialData, underwritingPageStore]);

      useEffect(() => {
        underwritingPageStore.setForm(form);
        return () => {
          underwritingPageStore.setForm(undefined);
        };
      }, []);

      useEffect(() => {
        /*
         * IMPORTANT:
         * Currently, we need to provide values for every property in all objects within arrays.
         * Otherwise, some nested fields may not be registered in the form.
         */
        setFormInitialValues(form, initialData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [initialData]);

      // TODO: fix problem with arrays
      const hasChangedValues = !isEmpty(getChangedFormValues(form, options.fields, initialData));

      if (hasChangedValues) {
        underwritingPageStore.showConfirmModal();
      } else {
        underwritingPageStore.hideConfirmationModal();
      }

      const disableSubmit = !form.isValid || !hasChangedValues || form.submitting;

      return (
        <>
          <Box sx={{ p: 3, position: 'relative' }}>
            <WrappedComponent {...(props as P)} form={form} />
            {form.submitting && (
              <FormOverlay>
                <CircularProgress />
              </FormOverlay>
            )}
          </Box>
          <FormFooter>
            <Button
              variant="contained"
              disabled={disableSubmit}
              onClick={() => form.submit()}
              data-testid="form-submit"
            >
              {form.submitting ? 'Saving...' : 'Save'}
            </Button>
          </FormFooter>
          <Snackbar
            open={notification.show}
            autoHideDuration={3000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseNotification} severity={notification.severity} variant="filled">
              {notification.message}
            </Alert>
          </Snackbar>
        </>
      );
    });
  };
};
